import { Box, Tooltip, type TooltipProps } from '@mui/material';
import {
    useLayoutEffect,
    useRef,
    type CSSProperties,
    type ReactNode,
} from 'react';

type DisabledHintProps = {
    /**
     * Why the control is unavailable. `undefined` means it is available: no
     * tooltip, and no wrapper tab stop ahead of the control. Always the
     * wrapper's accessible name.
     */
    title: string | undefined;
    /**
     * Shown above the reason while the control is unavailable, e.g. the
     * control's value. It describes the wrapper, whose name stays the
     * reason alone.
     */
    lead?: ReactNode;
    children: ReactNode;
    /** Layout for the wrapper span, for hosts whose control must fill the
     *  row (the bulk panels) rather than sit inline. */
    spanStyle?: CSSProperties;
    /**
     * Which way the reason opens. A blocked control is usually the last one
     * in its container, so upward is the form the reason is about.
     */
    placement?: TooltipProps['placement'];
    /**
     * Keep the wrapper while the control is available, so a gate that flips
     * mid-use does not remount the control, and focus moves between the
     * control and its hint as the gate closes and lifts. Not for a menu item:
     * its menu walks direct children for arrow keys and would skip the wrapper.
     */
    keepWrapper?: boolean;
};

/** Elements that can take the focus the hint hands over. */
const FOCUSABLE_CONTROL =
    'button, a[href], input, select, textarea, [tabindex]';

/**
 * Tooltip around a disabled control; the span keeps hover events alive because
 * disabled elements fire none themselves, and is focusable so the hint is
 * reachable without a pointer.
 *
 * A disabled MUI control takes no pointer events, so a plain `title` attribute
 * on it is never resolved by the browser: the reason has to hang on an enabled
 * wrapper. Passing a reason that may be absent is the normal case — a gate
 * reports one only while it blocks — so that is handled here rather than at
 * each call site.
 */
export default function DisabledHint({
    title,
    lead,
    children,
    spanStyle,
    keepWrapper = false,
    placement = 'bottom',
}: DisabledHintProps) {
    const spanRef = useRef<HTMLSpanElement>(null);
    const isBlocking = title !== undefined;

    // Focus follows the gate: onto the hint when the control it was on is
    // blocked, back to the control when the hint it was on lifts. Before
    // paint, so the focused element still reads as it was.
    useLayoutEffect(() => {
        const span = spanRef.current;
        const active = document.activeElement;
        if (span === null || active === null) {
            return;
        }
        if (isBlocking && active !== span && span.contains(active)) {
            span.focus();
        } else if (!isBlocking && active === span) {
            span.querySelector<HTMLElement>(FOCUSABLE_CONTROL)?.focus();
        }
    }, [isBlocking]);

    if (!isBlocking && !keepWrapper) {
        return <>{children}</>;
    }
    const describedByLead = isBlocking && lead !== undefined;
    return (
        <Tooltip
            title={
                describedByLead ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                        }}
                    >
                        <Box>{lead}</Box>
                        {/* Already the wrapper's name, so it is shown but
                            not read out again as its description. */}
                        <Box aria-hidden>{title}</Box>
                    </Box>
                ) : (
                    title
                )
            }
            placement={placement}
            describeChild={describedByLead}
        >
            <span
                ref={spanRef}
                tabIndex={isBlocking ? 0 : undefined}
                aria-label={title}
                style={spanStyle}
            >
                {children}
            </span>
        </Tooltip>
    );
}
